import { cloneDeep } from "lodash";
import { BOTTOM_BUTTONS, COLOR, Dart, DART_HITS, DART_NUMBER } from "../types/dart";
import { GAME_STATE } from "../types/game";
import { HalvesGameState, HalvesPlayerState, Round, ROUND_TYPE, HalvesPlayerRound } from "../types/halves";
import { PLAYER } from "../types/players";
import { allDifferentColors, getColor } from "./color";
import { playAudio } from "./sounds";

const initPlayerState: HalvesPlayerState = {
  name: '',
  score: 0,
  pastRounds: []
}; export

  const firstRound: Round = {
    type: ROUND_TYPE.ESTABLISH
  };

export const lastRound: Round = {
  type: ROUND_TYPE.BULLS,
  number: DART_NUMBER.BULL
};

export const defaultRounds: Round[] = [
  firstRound,
  {
    type: ROUND_TYPE.SEL_NUMBER,
    number: DART_NUMBER.TWENTY
  }, {
    type: ROUND_TYPE.SEL_NUMBER,
    number: DART_NUMBER.NINETEEN
  }, {
    type: ROUND_TYPE.TRIPLE,
    multiplier: DART_HITS.TRIPLE
  }, {
    type: ROUND_TYPE.SEL_NUMBER,
    number: DART_NUMBER.EIGHTEEN
  }, {
    type: ROUND_TYPE.SEL_NUMBER,
    number: DART_NUMBER.SEVENTEEN
  }, {
    type: ROUND_TYPE.DOUBLE,
    multiplier: DART_HITS.DOUBLE
  }, {
    type: ROUND_TYPE.SEL_NUMBER,
    number: DART_NUMBER.SIXTEEN
  },
  {
    type: ROUND_TYPE.IN_BED,
  },
  {
    type: ROUND_TYPE.SEL_NUMBER,
    number: DART_NUMBER.FIFTEEN
  },
  lastRound
];

export const additionalRounds: Round[] = [
  { type: ROUND_TYPE.SEL_COLOR, color: COLOR.RED },
  { type: ROUND_TYPE.SEL_COLOR, color: COLOR.BLACK },
  { type: ROUND_TYPE.COLOR_DIFF },
]

const initGameState: HalvesGameState = {
  rounds: [{
    type: ROUND_TYPE.ESTABLISH
  }],
  currentRound: {
    type: ROUND_TYPE.ESTABLISH
  },
  gameState: GAME_STATE.ACTIVE,
  throwNumber: 1,
  currentPlayer: '',
  players: []
};

export function getHalvesInitState(numOfPlayers: number, customRounds: Round[] = defaultRounds): HalvesGameState {
  const gameState = cloneDeep(initGameState);
  gameState.rounds = cloneDeep(customRounds);
  gameState.currentRound = customRounds[0];
  for (let i = 0; i < numOfPlayers; i++) {
    const player = cloneDeep(initPlayerState);
    player.name = `Player ${i + 1}`;
    // Start off with 60 points if no establishing score round first
    if (gameState.currentRound.type !== ROUND_TYPE.ESTABLISH) {
      player.score = 60;
    }
    gameState.players.push(player);
  }
  gameState.currentPlayer = gameState.players[0].name;
  return gameState;
}

function getInitRound(roundType: ROUND_TYPE): HalvesPlayerRound {
  const round: HalvesPlayerRound = {
    roundType,
    score: 0,
    darts: [],
    success: false,
    startingScore: 0,
    endingScore: 0,
  };
  return round
}

export function getNextHalvesState(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = cloneDeep(gameState);
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState) return newState;
  let currentRound = playerState.currentRound;
  if (!currentRound) {
    currentRound = getInitRound(gameState.currentRound.type);
    currentRound.startingScore = playerState.score;
  }
  playerState.currentRound = currentRound;

  if (dart.number === BOTTOM_BUTTONS.MISS || dart.number === BOTTOM_BUTTONS.END_TURN) {
    return getEndOrMissedState(dart, newState);
  }
  doRoundLogic(dart, newState);
  if (isGameOver(newState)) {
    newState.gameState = GAME_STATE.PENDING;
    if (playerState.currentRound?.success) {
      playerState.score = currentRound.score + currentRound.startingScore;
    } else {
      playerState.score = Math.ceil(playerState.score / 2);
    }
    currentRound.endingScore = playerState.score;
    playerState.pastRounds.push(playerState.currentRound);
    return newState;
  }
  newState.throwNumber = newState.throwNumber === 3 ? 1 : newState.throwNumber + 1;
  if (newState.throwNumber === 1) {
    return changeTurns(newState);
  }
  return newState;
};

function getEndOrMissedState(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState || !playerState.currentRound) return newState;
  playAudio(BOTTOM_BUTTONS.MISS);
  if (dart.number === BOTTOM_BUTTONS.MISS) {
    playerState.currentRound.darts.push(dart);
    if (isGameOver(newState)) {
      newState.gameState = GAME_STATE.PENDING;
      if (playerState.currentRound?.success) {
        playerState.score = playerState.currentRound.score + playerState.currentRound.startingScore;
      } else {
        playerState.score = Math.ceil(playerState.score / 2);
      }
      playerState.currentRound.endingScore = playerState.score;
      playerState.pastRounds.push(playerState.currentRound);
      return newState;
    }
    newState.throwNumber = newState.throwNumber === 3 ? 1 : newState.throwNumber + 1;
    if (newState.throwNumber === 1) {
      return changeTurns(newState)
    };
  } else if (dart.number === BOTTOM_BUTTONS.END_TURN) {
    const missedThrows = 3 - playerState.currentRound.darts.length;
    for (let i = 0; i < missedThrows; i++) {
      playerState.currentRound.darts.push({ number: BOTTOM_BUTTONS.MISS, multiplier: BOTTOM_BUTTONS.MISS });
    }
    newState.throwNumber = 3;
    if (isGameOver(newState)) {
      newState.gameState = GAME_STATE.PENDING;
      if (playerState.currentRound?.success) {
        playerState.score = playerState.currentRound.score + playerState.currentRound.startingScore;
      } else {
        playerState.score = Math.ceil(playerState.score / 2);
      }
      playerState.currentRound.endingScore = playerState.score;
      playerState.pastRounds.push(playerState.currentRound);
      return newState;
    }
    return changeTurns(newState);
  }
  return newState;
}

function doRoundLogic(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  let newState = { ...gameState };
  switch (gameState.currentRound.type) {
    case ROUND_TYPE.ESTABLISH:
      newState = doEstablishRound(dart, gameState);
      break;
    case ROUND_TYPE.SEL_SCORE:
      newState = doSelectScoreRound(dart, gameState);
      break;
    case ROUND_TYPE.DOUBLE:
    case ROUND_TYPE.TRIPLE:
    case ROUND_TYPE.SEL_NUMBER:
      newState = doNumOrMultiplierRound(dart, gameState);
      break;
    case ROUND_TYPE.SEL_COLOR:
      newState = doSelectColorRound(dart, gameState);
      break;
    case ROUND_TYPE.IN_BED:
      newState = doInBedRound(dart, gameState);
      break;
    case ROUND_TYPE.COLOR_DIFF:
      newState = doDiffColorRound(dart, gameState);
      break;
    case ROUND_TYPE.BULLS:
      newState = doBullsRound(dart, gameState);
      break;
  }
  return newState;
}

function doEstablishRound(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState) return newState;
  let currentRound = playerState.currentRound;
  if (!currentRound) return newState;
  currentRound.success = true;
  currentRound.darts.push(dart);
  currentRound.score += (dart.number * dart.multiplier);
  playerState.score = currentRound.startingScore + currentRound.score;
  playerState.currentRound = currentRound;
  playAudio(dart.multiplier);
  return newState;
}

function doSelectScoreRound(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState) return newState;
  let currentRound = playerState.currentRound;
  if (!currentRound) return newState;
  currentRound.darts.push(dart);
  currentRound.score += (dart.number * dart.multiplier);
  if (currentRound.darts.length === 3) {
    currentRound.success = currentRound.score === gameState.currentRound.score
  }
  playerState.score = currentRound.startingScore + currentRound.score;
  playAudio(dart.multiplier);
  return newState;
}

function doNumOrMultiplierRound(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState) return newState;
  let currentRound = playerState.currentRound;
  if (!currentRound) return newState;
  currentRound.darts.push(dart);
  if (newState.currentRound?.number && dart.number === newState.currentRound.number) {
    currentRound.success = true;
    currentRound.score += (dart.number * dart.multiplier);
    playAudio(dart.multiplier);
  } else if (newState.currentRound?.multiplier && dart.multiplier === newState.currentRound.multiplier) {
    currentRound.success = true;
    currentRound.score += (dart.number * dart.multiplier);
    playAudio(dart.multiplier);
  } else {
    playAudio(BOTTOM_BUTTONS.MISS);
  }
  playerState.score = currentRound.startingScore + currentRound.score;
  playerState.currentRound = currentRound;
  return newState;
}

function doSelectColorRound(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState) return newState;
  let currentRound = playerState.currentRound;
  if (!currentRound) return newState;
  if (getColor(dart) === gameState.currentRound.color) {
    currentRound.success = true;
    currentRound.score += (dart.number * dart.multiplier);
    playAudio(dart.multiplier);
  } else {
    playAudio(BOTTOM_BUTTONS.MISS);
  }
  playerState.score = currentRound.startingScore + currentRound.score;

  playerState.currentRound = currentRound;
  return newState;
}

function doInBedRound(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState) return newState;
  let currentRound = playerState.currentRound;
  if (!currentRound) return newState;
  currentRound.darts.push(dart);
  currentRound.score += (dart.number * dart.multiplier);
  playerState.score = currentRound.startingScore + currentRound.score;

  let dartNumber: DART_NUMBER | BOTTOM_BUTTONS = BOTTOM_BUTTONS.END_TURN;
  let success = true;
  for (let i = 0; i < currentRound.darts.length; i++) {
    const num = currentRound.darts[i].number;
    if (i === 0) {
      dartNumber = num;
      continue;
    }

    if (num !== dartNumber && num !== BOTTOM_BUTTONS.MISS) {
      success = false;
    }
  }
  currentRound.success = success;
  if (!success) {
    playerState.score = Math.ceil(currentRound.startingScore / 2);
  }
  if (!currentRound.success) {
    playAudio(BOTTOM_BUTTONS.MISS);
  } else {
    playAudio(dart.multiplier);
  }
  playerState.currentRound = currentRound;
  return newState;
}

function doDiffColorRound(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState) return newState;
  let currentRound = playerState.currentRound;
  if (!currentRound) return newState;
  currentRound.darts.push(dart);
  currentRound.success = allDifferentColors(currentRound.darts);
  playAudio(dart.multiplier);
  playerState.score = currentRound.startingScore + currentRound.score;
  playerState.currentRound = currentRound;
  return newState;
}

function doBullsRound(dart: Dart, gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  if (!playerState) return newState;
  let currentRound = playerState.currentRound;
  if (!currentRound) return newState;
  currentRound.darts.push(dart);
  if (dart.number === DART_NUMBER.BULL) {
    currentRound.success = true;
    currentRound.score += (dart.number * dart.multiplier);
    playAudio(dart.multiplier);
  } else {
    playAudio(BOTTOM_BUTTONS.MISS);
  }
  playerState.score = currentRound.startingScore + currentRound.score;
  playerState.currentRound = currentRound;
  return newState;
}


function isGameOver({ players, currentRound, currentPlayer, throwNumber, rounds }: HalvesGameState): boolean {
  const lastRound = rounds[rounds.length - 1];
  if (throwNumber !== 3 || currentRound.type !== lastRound.type) return false;
  return players[players.length - 1].name === currentPlayer
}

function changeTurns(gameState: HalvesGameState): HalvesGameState {
  const newState = { ...gameState };
  const { currentPlayer, players } = newState;
  const playerState = players.find(p => p.name === currentPlayer);
  const currentRound = playerState?.currentRound;
  if (!playerState || !currentRound) return newState;
  if (playerState.currentRound?.success) {
    playerState.score = currentRound.score + currentRound.startingScore;
  } else {
    playerState.score = Math.ceil(currentRound.startingScore / 2);
  }
  currentRound.endingScore = playerState.score;
  playerState.pastRounds.push(currentRound);
  const playerIdx = players.indexOf(playerState);
  const changeRounds = playerIdx === players.length - 1;
  if (changeRounds) {
    newState.currentRound = getNextRound(newState);
  }
  const nextPlayerIdx = changeRounds ? 0 : playerIdx + 1;
  delete newState.players[playerIdx].currentRound;
  newState.currentPlayer = players[nextPlayerIdx].name;
  newState.throwNumber = 1;

  return newState;
}

export function endGame(gameState: HalvesGameState): HalvesGameState {
  const newState = cloneDeep(gameState);
  newState.gameState = GAME_STATE.FINISHED;
  newState.winner = PLAYER.FIRST;
  return newState;
}

function getNextRound({ rounds, currentRound }: HalvesGameState): Round {
  let currentRoundIdx = getCurrentRoundIdx(currentRound, rounds);
  let nextRound: Round = { type: ROUND_TYPE.BULLS, number: DART_NUMBER.BULL };
  if (currentRoundIdx < 0 || currentRoundIdx === rounds.length - 1) return nextRound;
  nextRound = rounds[currentRoundIdx + 1];
  return nextRound;
}

export function getCurrentRoundIdx(currentRound: Round, rounds: Round[]): number {
  let currentRoundIdx = 0;
  for (let i = 0; i < rounds.length; i++) {
    const round = rounds[i];
    if (round.type !== currentRound.type) continue;
    if (!round.number && !round.color && !round.colors && !round.numbers && !round.score && !round.multiplier) {
      currentRoundIdx = i;
      break;
    }
    if (round.number && round.number === currentRound.number) {
      currentRoundIdx = i;
      break;
    }
    if (round.color && round.color === currentRound.color) {
      currentRoundIdx = i;
      break;
    }
    if (round.colors && round.colors === currentRound.colors) {
      currentRoundIdx = i;
      break;
    }
    if (round.numbers && round.numbers === currentRound.numbers) {
      currentRoundIdx = i;
      break;
    }
    if (round.score && round.score === currentRound.score) {
      currentRoundIdx = i;
      break;
    }
    if (round.multiplier && round.multiplier === currentRound.multiplier) {
      currentRoundIdx = i;
      break;
    }
  }
  return currentRoundIdx;
}